import { Box } from '@mui/material';
import { DocumentType, Media } from '@synergy/models';
import { FormattedDateTimeRange, FormattedMessage } from 'react-intl';
import { Formik, FormikProps } from 'formik';
import { PoolButton, PoolModal } from '@synergy/components';
import { PoolTypography } from '@synergy/components-next';
import { ReactElement, useEffect } from 'react';
import { removeUploadedFile, uploadFile } from '@synergy/data-access';

import { DemandAccountFeedback } from '../../../../models/demand-account-feedback';
import { DemandAccountItem } from '../../../../models/demand-account-item';
import { DemandAccountParticipant } from '../../../../models/demand-account-participant';
import { DemandSteps } from '../../models/demand-steps';
import { FeedbackDetailForm } from './feedback-detail-form';
import { demandDetailFeedbackDetailSelector } from '../../store/feedback/demand-detail-feedback-selectors';
import { demandDetailPositionsDeliverDateSelector } from '../../store/positions/demand-detail-positions-selectors';
import { demandDetailStepperStateSelector } from '../../store/demand-detail-selectors';
import { fetchFeedbackDetail, updateFeedback } from '../../store/feedback/demand-detail-feedback-actions';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

export interface DemandAccountFeedbackFormValues {
    items: DemandAccountItem[];
    internalNote?: string;
    media: Media[];
    noteFromCustomer?: string;
}

export interface FeedbackDetailFormProps {
    demandAccountParticipant?: DemandAccountParticipant;
    onClose: () => void;
}

export const FeedbackDetailFormModal = (props: FeedbackDetailFormProps): ReactElement | null => {
    const { demandAccountParticipant, onClose } = props;
    const dispatch = useThunkDispatch();
    const demandFeedback = useAppSelector(demandDetailFeedbackDetailSelector);
    const { deliveryDateFrom, deliveryDateTo } = useAppSelector(demandDetailPositionsDeliverDateSelector);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Feedback);

    useEffect(() => {
        if (demandAccountParticipant) {
            dispatch(fetchFeedbackDetail(demandAccountParticipant.id));
        }
    }, [dispatch, demandAccountParticipant]);

    const uploadMedia = (demandAccount: DemandAccountFeedback, newMedia: Media[]) => {
        const id = demandAccount.id;
        if (!id) {
            return;
        }
        newMedia.forEach(media => {
            if (media.file) {
                dispatch(uploadFile(DocumentType.DemandAccount, id, media.file));
            }
        });
    };

    const removeMedia = (demandAccount: DemandAccountFeedback, newMedia: Media[]) => {
        const existingMedia = (demandAccount && demandAccount.media) || [];
        const id = demandAccount.id;
        if (!id) {
            return;
        }
        // Get all media witch was removed in current state
        const removedMedia = existingMedia.filter(
            existingItem => !newMedia.some(media => media.id === existingItem.id)
        );
        removedMedia.forEach(item => dispatch(removeUploadedFile(DocumentType.DemandAccount, id, item)));
    };

    const handleSubmit = (values: DemandAccountFeedbackFormValues) => {
        if (!demandFeedback) {
            return;
        }

        removeMedia(demandFeedback, values.media);
        uploadMedia(demandFeedback, values.media);

        dispatch(updateFeedback(demandFeedback.id, values));

        props.onClose();
    };

    const handleCloseModal = (formikProps: FormikProps<DemandAccountFeedbackFormValues>) => {
        props.onClose();
        formikProps.resetForm();
    };

    const initialValues: DemandAccountFeedbackFormValues = {
        items: demandFeedback?.items ?? [],
        internalNote: demandFeedback?.internalNote,
        media: demandFeedback?.media ?? [],
        noteFromCustomer: demandFeedback?.noteFromCustomer ?? '',
    };

    const handleSaveOrClose = (formikProps: FormikProps<DemandAccountFeedbackFormValues>) => {
        if (!formikProps.dirty) {
            onClose();
            return;
        }

        formikProps.submitForm();
        onClose();
    };

    return (
        <Formik onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize={true}>
            {formikProps => (
                <PoolModal
                    handleClose={() => handleCloseModal(formikProps)}
                    isVisible={!!demandAccountParticipant}
                    actionChildren={[
                        !readonly ? (
                            <PoolButton
                                key="1"
                                variant="text"
                                styleType="firstLevel"
                                onClick={() => formikProps.resetForm()}
                            >
                                <FormattedMessage id="app.common.reset" />
                            </PoolButton>
                        ) : (
                            <span />
                        ),
                        <PoolButton key="2" styleType="firstLevel" onClick={() => handleSaveOrClose(formikProps)}>
                            <FormattedMessage id={formikProps.dirty ? 'app.common.save' : 'app.common.close'} />
                        </PoolButton>,
                    ]}
                    title={
                        <Box sx={{ position: 'relative', top: -16 }}>
                            <span>{demandFeedback?.customer?.name}</span>
                            {deliveryDateFrom && deliveryDateTo && (
                                <PoolTypography
                                    component={Box}
                                    translationId="app.common.deliveryDateRange"
                                    values={{
                                        value: (
                                            <FormattedDateTimeRange
                                                day="2-digit"
                                                month="short"
                                                year="numeric"
                                                from={deliveryDateFrom}
                                                to={deliveryDateTo}
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </Box>
                    }
                    width="lg"
                >
                    <FeedbackDetailForm />
                </PoolModal>
            )}
        </Formik>
    );
};
